import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import styles from './SeoInspector.module.css';

type MetaRecord = {
  key: string; // name/property/http-equiv
  value: string;
};

type LinkRecord = {
  rel: string;
  href: string;
};

type SeoSnapshot = {
  href: string;
  title: string;
  canonical?: string;
  description?: string;
  robots?: string;
  openGraph: MetaRecord[];
  twitter: MetaRecord[];
  otherMeta: MetaRecord[];
  links: LinkRecord[];
  jsonLd: Array<{ raw: string; parsed?: unknown; parseError?: string }>;
  warnings: string[];
};

const STORAGE_KEY = 'seoDebug';

function getEnabledFromUrl(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  const val = params.get('seoDebug');
  return val === '1' || val === 'true';
}

function getEnabledFromStorage(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function setEnabledInStorage(enabled: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // ignore
  }
}

function uniqByKeyKeepAll(items: MetaRecord[]): MetaRecord[] {
  // keep order; do not dedupe in display (we want to surface duplicates).
  return items;
}

function readSeoSnapshot(): SeoSnapshot {
  const href = typeof window !== 'undefined' ? window.location.href : '';
  const title = typeof document !== 'undefined' ? document.title : '';

  const metaNodes = typeof document !== 'undefined' ? Array.from(document.head.querySelectorAll('meta')) : [];
  const linkNodes = typeof document !== 'undefined' ? Array.from(document.head.querySelectorAll('link')) : [];
  const jsonLdNodes =
    typeof document !== 'undefined'
      ? Array.from(document.head.querySelectorAll('script[type="application/ld+json"]'))
      : [];

  const metas: MetaRecord[] = metaNodes
    .map((m) => {
      const key = m.getAttribute('name') || m.getAttribute('property') || m.getAttribute('http-equiv') || '';
      const value = m.getAttribute('content') || '';
      if (!key) return null;
      return { key, value };
    })
    .filter(Boolean) as MetaRecord[];

  const canonical =
    linkNodes.find((l) => (l.getAttribute('rel') || '').toLowerCase() === 'canonical')?.getAttribute('href') || undefined;

  const description = metas.find((m) => m.key.toLowerCase() === 'description')?.value;
  const robots = metas.find((m) => m.key.toLowerCase() === 'robots')?.value;

  const openGraph = metas.filter((m) => m.key.toLowerCase().startsWith('og:'));
  const twitter = metas.filter((m) => m.key.toLowerCase().startsWith('twitter:'));
  const otherMeta = metas.filter((m) => {
    const k = m.key.toLowerCase();
    return k !== 'description' && k !== 'robots' && !k.startsWith('og:') && !k.startsWith('twitter:');
  });

  const links: LinkRecord[] = linkNodes
    .map((l) => {
      const rel = (l.getAttribute('rel') || '').trim();
      const href = (l.getAttribute('href') || '').trim();
      if (!rel || !href) return null;
      return { rel, href };
    })
    .filter(Boolean) as LinkRecord[];

  const jsonLd = jsonLdNodes.map((s) => {
    const raw = (s.textContent || '').trim();
    if (!raw) return { raw: '' };
    try {
      const parsed = JSON.parse(raw);
      return { raw, parsed };
    } catch (e: any) {
      return { raw, parseError: e?.message || 'Invalid JSON' };
    }
  });

  const warnings: string[] = [];
  if (!title) warnings.push('Missing <title>');
  if (!description) warnings.push('Missing meta description');
  if (!canonical) warnings.push('Missing canonical URL');

  const ogTitleCount = openGraph.filter((m) => m.key.toLowerCase() === 'og:title').length;
  const ogDescCount = openGraph.filter((m) => m.key.toLowerCase() === 'og:description').length;
  const ogImgCount = openGraph.filter((m) => m.key.toLowerCase() === 'og:image').length;
  if (ogTitleCount === 0) warnings.push('Missing og:title');
  if (ogDescCount === 0) warnings.push('Missing og:description');
  if (ogImgCount === 0) warnings.push('Missing og:image');
  if (ogTitleCount > 1) warnings.push(`Duplicate og:title (${ogTitleCount})`);
  if (ogDescCount > 1) warnings.push(`Duplicate og:description (${ogDescCount})`);
  if (ogImgCount > 1) warnings.push(`Duplicate og:image (${ogImgCount})`);

  if (canonical && canonical.startsWith('%PUBLIC_URL%')) warnings.push('Canonical still contains %PUBLIC_URL% (should be absolute)');
  if (openGraph.some((m) => m.key.toLowerCase() === 'og:url' && m.value.includes('%PUBLIC_URL%')))
    warnings.push('og:url contains %PUBLIC_URL% (should be absolute)');

  return {
    href,
    title,
    canonical,
    description,
    robots,
    openGraph: uniqByKeyKeepAll(openGraph),
    twitter: uniqByKeyKeepAll(twitter),
    otherMeta: uniqByKeyKeepAll(otherMeta),
    links,
    jsonLd,
    warnings,
  };
}

function getOgValue(openGraph: MetaRecord[], key: string): string | undefined {
  return openGraph.find((m) => m.key.toLowerCase() === key.toLowerCase())?.value;
}

const OgPreviewTab: React.FC<{ snapshot: SeoSnapshot | null }> = ({ snapshot }) => {
  if (!snapshot) {
    return (
      <div className={styles.section}>
        <p className={styles.sectionTitle}>No data available</p>
        <p style={{ fontSize: '12px', opacity: 0.7 }}>Refresh to load SEO data</p>
      </div>
    );
  }

  const ogTitle = getOgValue(snapshot.openGraph, 'og:title') || snapshot.title || 'Untitled';
  const ogDescription = getOgValue(snapshot.openGraph, 'og:description') || snapshot.description || '';
  const ogImage = getOgValue(snapshot.openGraph, 'og:image');
  const ogUrl = getOgValue(snapshot.openGraph, 'og:url') || snapshot.canonical || snapshot.href;
  const ogType = getOgValue(snapshot.openGraph, 'og:type') || 'website';
  const ogSiteName = getOgValue(snapshot.openGraph, 'og:site_name') || '';

  // Extract domain from URL for display
  const domain = ogUrl ? new URL(ogUrl).hostname.replace(/^www\./, '') : '';

  return (
    <div className={styles.ogPreviewContainer}>
      <div className={styles.section}>
        <p className={styles.sectionTitle}>Social Media Preview</p>
        <p style={{ fontSize: '11px', opacity: 0.6, marginTop: '4px', marginBottom: '16px' }}>
          How your page will appear when shared on social platforms
        </p>

        <div className={styles.ogCard}>
          {ogImage ? (
            <div className={styles.ogImageContainer}>
              <img src={ogImage} alt={ogTitle} className={styles.ogImage} onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                if (target.parentElement) {
                  target.parentElement.innerHTML = '<div style="padding: 40px; text-align: center; color: #9ca3af; font-size: 12px;">Image failed to load</div>';
                }
              }} />
            </div>
          ) : (
            <div className={styles.ogImagePlaceholder}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <span>No image</span>
            </div>
          )}

          <div className={styles.ogContent}>
            <div className={styles.ogType}>{ogType}</div>
            <h3 className={styles.ogTitle}>{ogTitle}</h3>
            {ogDescription && <p className={styles.ogDescription}>{ogDescription}</p>}
            <div className={styles.ogFooter}>
              {ogSiteName && <span className={styles.ogSiteName}>{ogSiteName}</span>}
              {domain && <span className={styles.ogDomain}>{domain}</span>}
            </div>
          </div>
        </div>

        <div className={styles.ogMetadata}>
          <div className={styles.ogMetadataRow}>
            <span className={styles.ogMetadataLabel}>Image URL:</span>
            <span className={styles.ogMetadataValue}>{ogImage || 'Not set'}</span>
          </div>
          <div className={styles.ogMetadataRow}>
            <span className={styles.ogMetadataLabel}>URL:</span>
            <span className={styles.ogMetadataValue}>{ogUrl}</span>
          </div>
          <div className={styles.ogMetadataRow}>
            <span className={styles.ogMetadataLabel}>Type:</span>
            <span className={styles.ogMetadataValue}>{ogType}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

type TabType = 'overview' | 'og-preview';

const SeoInspector: React.FC = () => {
  const location = useLocation();

  const enabled = useMemo(() => {
    // Only enable in development mode
    if (process.env.NODE_ENV !== 'development') return false;
    // Enable via:
    // - ?seoDebug=1 (auto-persists)
    // - localStorage.seoDebug === "1"
    if (typeof window === 'undefined') return false;
    const urlEnabled = getEnabledFromUrl();
    if (urlEnabled) setEnabledInStorage(true);
    return urlEnabled || getEnabledFromStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const [isOpen, setIsOpen] = useState(false);
  const [snapshot, setSnapshot] = useState<SeoSnapshot | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const refresh = useCallback(() => {
    setSnapshot(readSeoSnapshot());
  }, []);

  useEffect(() => {
    if (!enabled) return;
    // Keep snapshot current on navigation, even if panel closed (cheap).
    refresh();
    // Close panel when route changes to avoid stale comparisons
    setIsOpen(false);
    // Reset to overview tab on navigation
    setActiveTab('overview');
  }, [enabled, location.pathname, location.search, refresh]);

  useEffect(() => {
    if (!enabled || !isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, isOpen]);

  const handleCopyReport = async () => {
    if (!snapshot) return;
    const report = JSON.stringify(snapshot, null, 2);
    try {
      await navigator.clipboard.writeText(report);
    } catch {
      // ignore
    }
  };

  if (!enabled) return null;

  const panel = isOpen ? (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) setIsOpen(false);
      }}
    >
      <div className={styles.panel}>
        <div className={styles.header}>
          <div className={styles.titleBlock}>
            <p className={styles.title}>SEO Inspector</p>
            <p className={styles.subtitle}>{snapshot?.href || ''}</p>
          </div>
          <div className={styles.actions}>
            <button className={styles.button} type="button" onClick={refresh}>
              Refresh
            </button>
            <button className={styles.button} type="button" onClick={handleCopyReport}>
              Copy JSON
            </button>
            <button className={styles.button} type="button" onClick={() => setIsOpen(false)}>
              Close
            </button>
          </div>
        </div>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'overview' ? styles.tabActive : ''}`}
            type="button"
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'og-preview' ? styles.tabActive : ''}`}
            type="button"
            onClick={() => setActiveTab('og-preview')}
          >
            OG Preview
          </button>
        </div>

        <div className={styles.content}>
          {activeTab === 'overview' ? (
            <>
              <div className={styles.section}>
                <p className={styles.sectionTitle}>Warnings</p>
                <div className={styles.pillRow}>
                  {(snapshot?.warnings || []).length === 0 ? (
                    <span className={`${styles.pill} ${styles.pillOk}`}>No obvious issues</span>
                  ) : (
                    snapshot?.warnings.map((w) => (
                      <span key={w} className={`${styles.pill} ${styles.pillWarn}`}>
                        {w}
                      </span>
                    ))
                  )}
                </div>
              </div>

              <div className={styles.section}>
                <p className={styles.sectionTitle}>Basics</p>
                <div className={styles.kv}>
                  <div className={styles.key}>title</div>
                  <div className={styles.value}>{snapshot?.title || ''}</div>

                  <div className={styles.key}>canonical</div>
                  <div className={styles.value}>{snapshot?.canonical || ''}</div>

                  <div className={styles.key}>description</div>
                  <div className={styles.value}>{snapshot?.description || ''}</div>

                  <div className={styles.key}>robots</div>
                  <div className={styles.value}>{snapshot?.robots || ''}</div>
                </div>
              </div>

              <div className={styles.section}>
                <p className={styles.sectionTitle}>Open Graph</p>
                <div className={styles.list}>
                  {(snapshot?.openGraph || []).length === 0 ? (
                    <div className={styles.metaRow}>
                      <div className={styles.metaName}>—</div>
                      <div className={styles.metaContent}>No og:* tags found</div>
                    </div>
                  ) : (
                    snapshot?.openGraph.map((m, idx) => (
                      <div key={`${m.key}-${idx}`} className={styles.metaRow}>
                        <div className={styles.metaName}>{m.key}</div>
                        <div className={styles.metaContent}>{m.value}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className={styles.section}>
                <p className={styles.sectionTitle}>Twitter</p>
                <div className={styles.list}>
                  {(snapshot?.twitter || []).length === 0 ? (
                    <div className={styles.metaRow}>
                      <div className={styles.metaName}>—</div>
                      <div className={styles.metaContent}>No twitter:* tags found</div>
                    </div>
                  ) : (
                    snapshot?.twitter.map((m, idx) => (
                      <div key={`${m.key}-${idx}`} className={styles.metaRow}>
                        <div className={styles.metaName}>{m.key}</div>
                        <div className={styles.metaContent}>{m.value}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className={styles.section}>
                <p className={styles.sectionTitle}>Other meta</p>
                <div className={styles.list}>
                  {(snapshot?.otherMeta || []).length === 0 ? (
                    <div className={styles.metaRow}>
                      <div className={styles.metaName}>—</div>
                      <div className={styles.metaContent}>No other meta tags found</div>
                    </div>
                  ) : (
                    snapshot?.otherMeta.map((m, idx) => (
                      <div key={`${m.key}-${idx}`} className={styles.metaRow}>
                        <div className={styles.metaName}>{m.key}</div>
                        <div className={styles.metaContent}>{m.value}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className={styles.section}>
                <p className={styles.sectionTitle}>Links</p>
                <div className={styles.list}>
                  {(snapshot?.links || []).length === 0 ? (
                    <div className={styles.metaRow}>
                      <div className={styles.metaName}>—</div>
                      <div className={styles.metaContent}>No link tags found</div>
                    </div>
                  ) : (
                    snapshot?.links.map((l, idx) => (
                      <div key={`${l.rel}-${idx}`} className={styles.metaRow}>
                        <div className={styles.metaName}>{l.rel}</div>
                        <div className={styles.metaContent}>{l.href}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className={styles.section}>
                <p className={styles.sectionTitle}>JSON-LD</p>
                <div className={styles.list}>
                  {(snapshot?.jsonLd || []).length === 0 ? (
                    <div className={styles.metaRow}>
                      <div className={styles.metaName}>—</div>
                      <div className={styles.metaContent}>No application/ld+json scripts found</div>
                    </div>
                  ) : (
                    snapshot?.jsonLd.map((j, idx) => (
                      <div key={idx} className={styles.code}>
                        {j.parseError ? `Parse error: ${j.parseError}\n\n` : ''}
                        {j.raw}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className={styles.section}>
                <p className={styles.sectionTitle}>Disable</p>
                <button
                  className={styles.button}
                  type="button"
                  onClick={() => {
                    setEnabledInStorage(false);
                    setIsOpen(false);
                    // Force navigation refresh without query param if it was used
                    window.location.reload();
                  }}
                >
                  Disable SEO Inspector (clears localStorage)
                </button>
              </div>
            </>
          ) : (
            <OgPreviewTab snapshot={snapshot} />
          )}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        type="button"
        className={styles.fab}
        onClick={() => {
          if (!snapshot) refresh();
          setIsOpen(true);
        }}
        aria-label="Open SEO inspector"
        title="Open SEO inspector"
      >
        SEO
      </button>
      {panel ? createPortal(panel, document.body) : null}
    </>
  );
};

export default SeoInspector;

